import type { ReactNode } from "react";
import { AlertTriangle } from "lucide-react";

interface TableSkeletonProps {
  rows?: number;
  rowHeight?: number;
}

/** N animated skeleton rows for use while a list/table loads. */
export function TableSkeleton({ rows = 4, rowHeight = 48 }: TableSkeletonProps) {
  return (
    <div className="space-y-1.5">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          style={{ height: rowHeight }}
          className="animate-pulse rounded-md border border-border-primary bg-bg-secondary"
        />
      ))}
    </div>
  );
}

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  body?: ReactNode;
  action?: ReactNode;
  /** Use dashed border for "nothing here yet"; solid for "expected absence". */
  variant?: "dashed" | "solid";
}

export function EmptyState({
  icon,
  title,
  body,
  action,
  variant = "dashed",
}: EmptyStateProps) {
  return (
    <div
      className={`rounded-md ${variant === "dashed" ? "border-dashed" : ""} border border-border-primary bg-bg-secondary px-4 py-12 text-center`}
    >
      <div className="mx-auto flex max-w-prose flex-col items-center gap-2">
        {icon}
        <p className="text-sm font-medium text-text-primary">{title}</p>
        {body && <div className="text-sm text-text-secondary">{body}</div>}
        {action && <div className="mt-2">{action}</div>}
      </div>
    </div>
  );
}

/**
 * Warning shown on webhook-integrator views while the project is deactivated —
 * the ingest route only accepts webhooks for active projects (wh.ts joins on
 * p.active = true), so incoming requests are rejected and lost.
 */
export function ProjectPausedWebhookBanner() {
  return (
    <div className="flex items-start gap-2 rounded-md border border-warning bg-warning-bg px-3 py-2 text-xs text-warning">
      <AlertTriangle size={14} className="mt-0.5 shrink-0" />
      <p>
        <strong>Projekt ist pausiert</strong> — eingehende Webhooks werden
        abgewiesen und gehen verloren (es werden auch keine Samples
        aufgezeichnet), bis das Projekt wieder aktiviert wird.
      </p>
    </div>
  );
}
